export enum Role {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
}

export enum PropertyStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  SOLD = 'SOLD',
  RENTED = 'RENTED',
  AVAILABLE = 'AVAILABLE',
}

export enum PropertyType {
  APARTMENT = 'APARTMENT',
  STUDIO = 'STUDIO',
  DUPLEX = 'DUPLEX',
  TRIPLEX = 'TRIPLEX',
  PENTHOUSE = 'PENTHOUSE',
  VILLA = 'VILLA',
  TOWNHOUSE = 'TOWNHOUSE',
  BUILDING = 'BUILDING',
  UNIT = 'UNIT',
  OFFICE = 'OFFICE',
  WAREHOUSE = 'WAREHOUSE',
  SHOP = 'SHOP',
  SHOWROOM = 'SHOWROOM',
  LAND = 'LAND',
  COMMERCIAL = 'COMMERCIAL',
  OTHER = 'OTHER',
}

export enum ActivityType {
  PROPERTY_CREATED = 'PROPERTY_CREATED',
  PROPERTY_UPDATED = 'PROPERTY_UPDATED',
  PROPERTY_APPROVED = 'PROPERTY_APPROVED',
  PROPERTY_REJECTED = 'PROPERTY_REJECTED',
  PROPERTY_DELETED = 'PROPERTY_DELETED',
  AGENT_CREATED = 'AGENT_CREATED',
  AGENT_UPDATED = 'AGENT_UPDATED',
  AGENT_DELETED = 'AGENT_DELETED',
  CLIENT_CREATED = 'CLIENT_CREATED',
  CLIENT_UPDATED = 'CLIENT_UPDATED',
  CLIENT_DELETED = 'CLIENT_DELETED',
  PROJECT_CREATED = 'PROJECT_CREATED',
  PROJECT_UPDATED = 'PROJECT_UPDATED',
}

export enum NotificationType {
  PROPERTY_APPROVAL = 'PROPERTY_APPROVAL',
  PERMISSION_REQUEST = 'PERMISSION_REQUEST',
  AGENT_REQUEST = 'AGENT_REQUEST',
  AGENT_ASSIGNED = 'AGENT_ASSIGNED',
  BIRTHDAY = 'BIRTHDAY',
}

export enum NotificationStatus {
  UNREAD = 'UNREAD',
  READ = 'READ',
}

export enum UnitStatus {
  AVAILABLE = 'AVAILABLE',
  RESERVED = 'RESERVED',
  SOLD = 'SOLD',
  RENTED = 'RENTED',
}

export enum ProjectStatus {
  PLANNING = 'PLANNING',
  UNDER_CONSTRUCTION = 'UNDER_CONSTRUCTION',
  COMPLETED = 'COMPLETED',
  ON_HOLD = 'ON_HOLD',
  CANCELLED = 'CANCELLED',
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: Role;
  permissions: string[];
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  agentProfile?: Agent;
}

export interface Agent {
  id: string;
  userId: string;
  user?: User;
  commission: number;
  rating: number;
  totalSales: number;
  properties?: Property[];
  clients?: Client[];
  createdAt: string;
  updatedAt: string;
}

export interface Property {
  id: string;
  title: string;
  description?: string;
  latitude: number;
  longitude: number;
  address: string;
  city: string;
  country: string;
  postalCode?: string;
  governorate?: string;
  district?: string;
  town?: string;
  propertyType: PropertyType;
  status: PropertyStatus;
  price: number;
  size?: number;
  bedrooms?: number;
  bathrooms?: number;
  propertyAge?: number;
  paymentMethod?: string;
  transactionType?: string;
  images: string[];
  features: string[];
  agentId?: string;
  agent?: Agent;
  // Owner/contact information
  ownerName?: string;
  ownerPhone?: string;
  // Optional Google Maps share link for exact location
  googleMapsLink?: string;
  // Additional property details
  floor?: number;
  totalFloors?: number;
  elevator?: string;
  maidRoom?: string;
  furnished?: string;
  parking?: string;
  waterTanks?: string;
  // Project / building unit link
  projectId?: string;
  project?: Project;
  floorNumber?: number;
  unitNumber?: string;
  unitStatus?: UnitStatus;
  createdById: string;
  createdBy?: User;
  approvedById?: string;
  approvedBy?: User;
  approvedAt?: string;
  rejectionReason?: string;
  soldBy?: 'COMPANY' | 'ANOTHER_COMPANY' | string;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  preferences?: string;
  budget?: number;
  minBudget?: number;
  maxBudget?: number;
  propertyTypes?: PropertyType[];
  transactionType?: string;
  buildingAge?: number;
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  paymentMethod?: string;
  // Location preferences
  governorate?: string;
  district?: string;
  town?: string;
  agentIds?: string[];
  agents?: Agent[];
  notes?: string;
  isCalled?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  startDate: string;
  endDate?: string;
  status: ProjectStatus;
  progress: number;
  // Owner information
  ownerName: string;
  ownerPhone: string;
  // Location
  latitude?: number;
  longitude?: number;
  governorate?: string;
  district?: string;
  town?: string;
  address?: string;
  city?: string;
  googleMapsLink?: string;
  // Building details
  totalFloors?: number;
  totalApartments?: number;
  availableApartments?: number;
  // Construction status
  constructionStatus: 'UNDER_CONSTRUCTION' | 'COMPLETED';
  completionPercentage?: number;
  // Linked apartments / units
  properties?: Property[];
  createdAt: string;
  updatedAt: string;
  user?: User;
  createdById?: string;
}

export interface ActivityLog {
  id: string;
  userId: string;
  user?: User;
  activityType: ActivityType;
  description: string;
  metadata?: string;
  ipAddress?: string;
  createdAt: string;
}

export interface Notification {
  id: string;
  type: NotificationType;
  senderId: string;
  sender?: User;
  recipientId?: string | null;
  recipient?: User | null;
  relatedEntityId?: string | null;
  message: string;
  status: NotificationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role?: Role;
}

export interface PropertyFormData {
  title: string;
  description?: string;
  latitude: number;
  longitude: number;
  address: string;
  city: string;
  country?: string;
  postalCode?: string;
  governorate?: string;
  district?: string;
  town?: string;
  propertyType: PropertyType;
  price: number;
  size?: number;
  bedrooms?: number;
  bathrooms?: number;
  images?: string[];
  features?: string[];
  ownerName?: string;
  ownerPhone?: string;
  googleMapsLink?: string;
  floor?: number;
  totalFloors?: number;
  elevator?: string;
  maidRoom?: string;
  furnished?: string;
  parking?: string;
  waterTanks?: string;
  // Project / building unit link
  projectId?: string;
  floorNumber?: number;
  unitNumber?: string;
  unitStatus?: UnitStatus;
}

export interface ClientFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferences?: string;
  budget?: number;
  notes?: string;
}

// Returned by GET /projects/:id/floors
export interface FloorUnit {
  id: string;
  title: string;
  unitNumber?: string;
  propertyType: PropertyType;
  unitStatus: UnitStatus;
  price: number;
  floorNumber?: number;
}

export interface FloorSummary {
  floor: number;
  units: FloorUnit[];
  available: number;
  reserved: number;
  sold: number;
  rented: number;
}

export interface BuildingCaretaker {
  id: string;
  name: string;
  phone: string;
  governorate?: string;
  district?: string;
  town?: string;
  buildingName?: string;
  notes?: string;
  agents?: Agent[];
  createdAt: string;
  updatedAt: string;
}

export interface BuildingCaretakerFormData {
  name: string;
  phone: string;
  governorate?: string;
  district?: string;
  town?: string;
  buildingName?: string;
  notes?: string;
}

export interface Broker {
  id: string;
  name: string;
  phone: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BrokerFormData {
  name: string;
  phone: string;
  notes?: string;
}

export interface ProjectFormData {
  name: string;
  description?: string;
  startDate: string;
  endDate?: string;
  status?: ProjectStatus;
  progress?: number;
  ownerName: string;
  ownerPhone: string;
  latitude?: number;
  longitude?: number;
  governorate?: string;
  district?: string;
  town?: string;
  address?: string;
  city?: string;
  googleMapsLink?: string;
  totalFloors?: number;
  totalApartments?: number;
  availableApartments?: number;
  constructionStatus: 'UNDER_CONSTRUCTION' | 'COMPLETED';
  completionPercentage?: number;
}
